/**
 * Consult Scheduling — Door A "Book Consult" panel.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHAT THIS IS
 * ─────────────────────────────────────────────────────────────────────────
 *  The staff/assistant slot picker from mockup Screen 1 (desktop) + Screen 8
 *  (mobile): a self-contained, controlled panel a staffer opens INSIDE a lead
 *  record, mid-call, to book a discovery consult for the client.
 *
 *  It is the credential-free Stage-1 build:
 *    · slots are REAL — produced by the pure `computeOpenSlots` engine against
 *      the seeded DEFAULT_AVAILABILITY windows/rules for the next ~14 days;
 *    · free/busy is the `fetchFreeBusy` seam (returns [] until the Google CF
 *      lands) so every in-window slot shows for now;
 *    · "Book →" calls the `bookConsultation` PLACEHOLDER seam, which writes
 *      nothing and returns an honest "calendar setup pending" state.
 *
 *  Design system: every value is a portal token via styles/consultPanel.css
 *  (`.cs-*` under `.cs-root`). Accessibility: native <select> for type, an
 *  ARIA radiogroup for the audience toggle (arrow-key navigable), aria-pressed
 *  slot buttons, a role="status" booking banner, and a 2px accent focus ring.
 *
 *  Parallel-safe: NEW file only. Not yet wired into Leads.tsx — see the feature
 *  README for the flag-gated drop-in snippet (a coordinated later step).
 * ─────────────────────────────────────────────────────────────────────────
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  DEFAULT_AVAILABILITY,
  type AppointmentType,
  type AvailabilitySettings,
} from '../lib/types';
import {
  computeOpenSlots,
  type FreeBusyInterval,
} from '../lib/availability';
import {
  bookConsultation,
  fetchBookedSlotStarts,
  fetchFreeBusy,
  listenAppointmentTypes,
  listenAvailabilitySettings,
  type BookConsultationResult,
  type ConsultMode,
} from '../lib/consultStore';
import {
  audienceLabel,
  describeSlot,
  dropBookedSlots,
  filterSlotsByAudience,
  groupSlotsByDay,
  type PickableAudience,
} from '../lib/slotView';
import '../styles/consultPanel.css';

/** The minimal lead-shaped data the panel needs (structural — no coupling to leads.ts). */
export interface ConsultLeadLike {
  /** Lead id, e.g. "C00407". Stamped onto the booking. */
  id: string;
  /** Display name, e.g. "Sumit". */
  name: string;
  /** City, shown for context, e.g. "Raipur". */
  city?: string | null;
  /** Project type, e.g. "New home". */
  projectType?: string | null;
  /** Default audience guess for the toggle. */
  audience?: PickableAudience;
}

export interface BookConsultPanelProps {
  /** The lead this consult is being booked for. */
  lead: ConsultLeadLike;
  /**
   * Appointment types for the Type dropdown. When omitted the panel subscribes
   * via `listenAppointmentTypes` (live, active types). The harness passes mocks.
   */
  appointmentTypes?: AppointmentType[];
  /**
   * Availability windows + rules. When omitted the panel subscribes via
   * `listenAvailabilitySettings`, which seeds with DEFAULT_AVAILABILITY.
   */
  availability?: AvailabilitySettings;
  /** Host consultant display name (mockup "with Rohit"). */
  hostName?: string;
  /** Who is doing the booking — the "Booked by" chip. */
  bookedByName?: string;
  /** Clock injection for deterministic preview/tests. Defaults to now. */
  nowISO?: string;
  /** How many days of slots to surface. Default 14. */
  horizonDays?: number;
  /** Fired after a REAL booked outcome (the host can refresh / toast). */
  onBooked?: (result: BookConsultationResult) => void;
}

/* ── tiny inline icons (stroke = currentColor; decorative → aria-hidden) ── */
const IcCalendar = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    aria-hidden="true"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </svg>
);
const IcCheck = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    aria-hidden="true"
  >
    <path d="M20 6L9 17l-5-5" />
  </svg>
);
const IcArrow = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    aria-hidden="true"
  >
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);
const IcChevron = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden="true"
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);

/** Initials for the "Booked by" mini-avatar ("Scheduling assistant" → "SA"). */
function initialsOf(name: string): string {
  return (
    name
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? '')
      .join('') || '?'
  );
}

const AUDIENCES: PickableAudience[] = ['indian', 'international'];
const AUDIENCE_FLAG: Record<PickableAudience, string> = {
  indian: '🇮🇳',
  international: '🌎',
};

/** How the consult happens — Mack's createConsultBooking contract vocabulary. */
const CONSULT_TYPES: Array<{ id: ConsultMode; label: string }> = [
  { id: 'video', label: 'Google Meet video' },
  { id: 'call', label: 'Phone call' },
  { id: 'office', label: 'AV Office' },
  { id: 'site', label: "Site / Client's place" },
];

export function BookConsultPanel({
  lead,
  appointmentTypes,
  availability,
  hostName = 'your consultant',
  bookedByName = 'Scheduling assistant',
  nowISO,
  horizonDays = 14,
  onBooked,
}: BookConsultPanelProps) {
  // Stable clock for the lifetime of the panel (deterministic in preview/tests).
  const [now] = useState(() => nowISO ?? new Date().toISOString());

  // Appointment types: controlled by prop, else live subscription.
  const [liveTypes, setLiveTypes] = useState<AppointmentType[]>([]);
  useEffect(() => {
    if (appointmentTypes) return; // controlled — no subscription
    const unsub = listenAppointmentTypes(setLiveTypes);
    return unsub;
  }, [appointmentTypes]);
  const types = appointmentTypes ?? liveTypes;

  // Availability: controlled by prop, else live subscription (seeded default).
  const [liveAvail, setLiveAvail] =
    useState<AvailabilitySettings>(DEFAULT_AVAILABILITY);
  useEffect(() => {
    if (availability) return;
    const unsub = listenAvailabilitySettings(setLiveAvail);
    return unsub;
  }, [availability]);
  const avail = availability ?? liveAvail;

  // Free/busy seam → Mack's getConsultAvailability callable. Degrades to an
  // empty calendar (status 'unavailable') if the CF isn't deployed yet.
  const [busy, setBusy] = useState<FreeBusyInterval[]>([]);
  const [availNote, setAvailNote] = useState<string | null>(null);
  // Double-booking guard (Vex, 2026-07-03) — slots that already carry a
  // consultations doc are TAKEN (idempotency is per lead+slot server-side, so
  // the calendar alone can miss just-booked slots). Refreshed after a booking.
  const [bookedStarts, setBookedStarts] = useState<string[]>([]);
  const [bookedNonce, setBookedNonce] = useState(0);
  // Bumped by the Retry control so a failed/timed-out availability load can be
  // re-attempted (no dead control). `availLoading` drives the Retry button copy.
  const [availNonce, setAvailNonce] = useState(0);
  const [availLoading, setAvailLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    setAvailLoading(true);
    const endISO = new Date(
      Date.parse(now) + horizonDays * 86_400_000,
    ).toISOString();
    // fetchFreeBusy is bounded (12s) + never throws, so this always settles —
    // the picker can never sit on an eternal load.
    void fetchFreeBusy(now, endISO)
      .then((res) => {
        if (!alive) return;
        setBusy(res.busy);
        setAvailNote(res.status === 'unavailable' ? (res.message ?? null) : null);
      })
      .catch(() => {
        if (alive) setAvailNote("Couldn't load available times just now.");
      })
      .finally(() => {
        if (alive) setAvailLoading(false);
      });
    void fetchBookedSlotStarts(now, endISO).then((starts) => {
      if (alive) setBookedStarts(starts);
    });
    return () => {
      alive = false;
    };
  }, [now, horizonDays, bookedNonce, availNonce]);
  const retryAvailability = () => setAvailNonce((n) => n + 1);

  // Selections.
  const [selectedTypeId, setSelectedTypeId] = useState<string>('');
  const [audience, setAudience] = useState<PickableAudience>(
    lead.audience ?? 'indian',
  );
  const [selectedStart, setSelectedStart] = useState<string | null>(null);
  // Phase 2 — how the consult happens (contract 'video'|'call') + an optional
  // note carried onto the calendar event / consultation doc.
  const [consultType, setConsultType] = useState<ConsultMode>('video');
  // Optional site address, only meaningful/sent for consultType==='site'.
  const [siteLocation, setSiteLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [phase, setPhase] = useState<'idle' | 'booking'>('idle');
  const [result, setResult] = useState<BookConsultationResult | null>(null);

  // Default the type to the first available once types arrive.
  useEffect(() => {
    if (!selectedTypeId && types.length > 0) setSelectedTypeId(types[0].id);
  }, [types, selectedTypeId]);

  const selectedType = types.find((t) => t.id === selectedTypeId);

  // Resolved booking parameters (type override → settings default).
  const durationMin = selectedType?.defaultDurationMin ?? avail.defaultSlotMin;
  const bufferMin =
    selectedType?.defaultBufferMin ?? avail.defaultBufferMin;
  const customWeekly =
    selectedType?.availability.mode === 'custom'
      ? selectedType.availability.weekly
      : null;
  const blockedDates = useMemo(
    () => avail.hardBlocks.map((b) => b.date),
    [avail.hardBlocks],
  );

  // The pure engine produces every in-window slot; then we filter by audience.
  const allSlots = useMemo(
    () =>
      computeOpenSlots(busy, avail.weekly, customWeekly, {
        durationMin,
        bufferMin,
        minNoticeHours: avail.minNoticeHours,
        maxPerDay: avail.maxPerDay,
        horizonDays,
        nowISO: now,
        timeZone: avail.timeZone,
        blockedDates,
      }),
    [
      busy,
      avail,
      customWeekly,
      durationMin,
      bufferMin,
      horizonDays,
      now,
      blockedDates,
    ],
  );
  const slots = useMemo(
    () => dropBookedSlots(filterSlotsByAudience(allSlots, audience), bookedStarts),
    [allSlots, audience, bookedStarts],
  );
  const dayGroups = useMemo(
    () => groupSlotsByDay(slots, avail.timeZone),
    [slots, avail.timeZone],
  );

  // Drop a selection that fell out of the current (re-filtered) list.
  useEffect(() => {
    if (selectedStart && !slots.some((s) => s.startISO === selectedStart)) {
      setSelectedStart(null);
    }
  }, [slots, selectedStart]);

  const tzLabel = avail.timeZone === 'Asia/Kolkata' ? 'IST' : avail.timeZone;
  const priceLabel = selectedType
    ? selectedType.defaultPrice.mode === 'free'
      ? 'Free'
      : `${selectedType.defaultPrice.currency ?? ''} ${selectedType.defaultPrice.amount ?? ''}`.trim()
    : '';
  /* ── SHARED radiogroup keyboard nav (← → / ↑ ↓ roving tabindex) ──
   * Vera HIGH-1 (2026-07-04): the consult-type group copied the audience
   * toggle's roving-tabindex markup but not its arrow-key handler — the
   * unchecked option is tabIndex=-1, so keyboard users could never switch
   * Meet ↔ Phone. ONE parameterised helper now drives BOTH radiogroups. */
  const segRef = useRef<HTMLDivElement>(null);
  const howRef = useRef<HTMLDivElement>(null);
  function segKeyDown<T extends string>(
    options: readonly T[],
    value: T,
    set: (next: T) => void,
    ref: React.RefObject<HTMLDivElement>,
  ) {
    return (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key))
        return;
      e.preventDefault();
      const dir = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : -1;
      const idx = options.indexOf(value);
      const next = options[(idx + dir + options.length) % options.length];
      set(next);
      const buttons =
        ref.current?.querySelectorAll<HTMLButtonElement>('[role="radio"]');
      buttons?.[options.indexOf(next)]?.focus();
    };
  }
  const onSegKeyDown = segKeyDown(AUDIENCES, audience, (a) => {
    setAudience(a);
    setResult(null);
  }, segRef);
  const CONSULT_TYPE_IDS = CONSULT_TYPES.map((t) => t.id) as ReadonlyArray<ConsultMode>;
  const onHowKeyDown = segKeyDown(CONSULT_TYPE_IDS, consultType, (t) => {
    setConsultType(t);
    setResult(null);
  }, howRef);

  async function onBook() {
    if (!selectedStart || !selectedType) return;
    const slot = slots.find((s) => s.startISO === selectedStart);
    if (!slot) return;
    setPhase('booking');
    setResult(null);
    const res = await bookConsultation({
      leadId: lead.id,
      typeId: selectedType.id,
      slotStartISO: slot.startISO,
      slotEndISO: slot.endISO,
      audience,
      timeZone: avail.timeZone,
      consultType,
      durationMin,
      ...(notes.trim() ? { notes: notes.trim() } : {}),
      // Optional site address rides along only for a site visit; blank is fine.
      ...(consultType === 'site' && siteLocation.trim()
        ? { siteLocation: siteLocation.trim() }
        : {}),
    });
    setResult(res);
    setPhase('idle');
    if (res.ok && res.status === 'booked') {
      // the slot is taken now — refresh the booked set so it leaves the picker.
      setBookedNonce((n) => n + 1);
      setSelectedStart(null);
      onBooked?.(res);
    }
  }

  return (
    <div className="cs-root">
      <section
        className="cs-panel"
        aria-label={`Book a consultation for ${lead.name}`}
      >
        <header className="cs-head">
          <div className="cs-head-title">
            <IcCalendar />
            Book Consult
          </div>
          {/* Live: the Google connection + booking CF are wired — availability
              loads and bookings create real Calendar events + Meet links on
              vastuarchitect@aayatanveda.com (confirmed live 2026-07). */}
          <div className="cs-live">
            <span className="cs-blip" aria-hidden="true" />
            Live · vastuarchitect@aayatanveda.com
          </div>
        </header>

        <div className="cs-body">
          <div className="cs-subhead">
            <div className="cs-who-row">
              <span className="cs-booker">
                Booked by:&nbsp;
                <span className="cs-mini-av" aria-hidden="true">
                  {initialsOf(bookedByName)}
                </span>
                &nbsp;{bookedByName}
              </span>
              <span className="cs-with">
                {selectedType ? (
                  <>
                    {selectedType.defaultPrice.mode === 'free'
                      ? 'Complimentary '
                      : ''}
                    {durationMin}-min {selectedType.name.toLowerCase()} · with{' '}
                    <b>{hostName}</b>
                  </>
                ) : (
                  <>
                    {durationMin}-min consultation · with <b>{hostName}</b>
                  </>
                )}
              </span>
            </div>
          </div>

          <div className="cs-controls">
            <div className="cs-ctrl">
              <label className="cs-label" htmlFor="cs-type">
                Type
              </label>
              <select
                id="cs-type"
                className="cs-select"
                value={selectedTypeId}
                disabled={types.length === 0}
                onChange={(e) => {
                  setSelectedTypeId(e.target.value);
                  setSelectedStart(null);
                  setResult(null);
                }}
              >
                {types.length === 0 ? (
                  <option value="">Loading types…</option>
                ) : (
                  types.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} ({t.defaultDurationMin} min ·{' '}
                      {t.defaultPrice.mode === 'free'
                        ? 'Free'
                        : `${t.defaultPrice.currency ?? ''} ${t.defaultPrice.amount ?? ''}`.trim()}
                      )
                    </option>
                  ))
                )}
              </select>
            </div>

            <div className="cs-ctrl cs-ctrl-auto">
              <span className="cs-label" id="cs-aud-label">
                Client
              </span>
              <div
                className="cs-seg"
                role="radiogroup"
                aria-labelledby="cs-aud-label"
                ref={segRef}
                onKeyDown={onSegKeyDown}
              >
                {AUDIENCES.map((a) => {
                  const checked = audience === a;
                  return (
                    <button
                      key={a}
                      type="button"
                      role="radio"
                      aria-checked={checked}
                      tabIndex={checked ? 0 : -1}
                      className="cs-seg-opt"
                      onClick={() => {
                        setAudience(a);
                        setResult(null);
                      }}
                    >
                      <span aria-hidden="true">{AUDIENCE_FLAG[a]}</span>{' '}
                      {audienceLabel(a)}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="cs-ctrl cs-ctrl-auto">
              <span className="cs-label" id="cs-how-label">
                How
              </span>
              {/* Mack's createConsultBooking contract vocabulary
                  ('video' | 'call' | 'office' | 'site'). Same radiogroup pattern
                  as the audience toggle; video mints a Meet link server-side,
                  site reveals an optional address field below. */}
              <div
                className="cs-seg"
                role="radiogroup"
                aria-labelledby="cs-how-label"
                ref={howRef}
                onKeyDown={onHowKeyDown}
              >
                {CONSULT_TYPES.map((t) => {
                  const checked = consultType === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      role="radio"
                      aria-checked={checked}
                      tabIndex={checked ? 0 : -1}
                      className="cs-seg-opt"
                      onClick={() => {
                        setConsultType(t.id);
                        setResult(null);
                      }}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="cs-ctrl cs-ctrl-auto">
              <span className="cs-label">Show times in</span>
              <span className="cs-tz-pill" title="Timezone switching coming soon">
                <b>{tzLabel}</b>{' '}
                {avail.timeZone === 'Asia/Kolkata' ? '(Asia/Kolkata)' : ''}
                <IcChevron />
              </span>
            </div>
          </div>

          {/* Location detail for the two in-person modes. Office = address is
              backend-supplied (no input). Site = an OPTIONAL free-text address;
              booking is NEVER blocked when it's blank (client just sees "Your
              site"). Hidden for video/call; siteLocation is only sent for site. */}
          {consultType === 'site' && (
            <div className="cs-ctrl" style={{ marginTop: 10 }}>
              <label className="cs-label" htmlFor="cs-site">
                Site address (optional)
              </label>
              <input
                id="cs-site"
                className="cs-select"
                type="text"
                value={siteLocation}
                placeholder="Paste the site address so it's on the calendar — or leave blank"
                onChange={(e) => {
                  setSiteLocation(e.target.value);
                  setResult(null);
                }}
                aria-describedby="cs-site-hint"
              />
              <span id="cs-site-hint" className="cs-fieldhint">
                {siteLocation.trim()
                  ? "This goes on the calendar event so everyone has the address."
                  : "Optional — leave blank and the client just sees “Your site.”"}
              </span>
            </div>
          )}
          {consultType === 'office' && (
            <p className="cs-fieldhint" style={{ marginTop: 8 }}>
              Client will get the AV office address + map link.
            </p>
          )}

          {/* Phase 2 — optional note carried onto the calendar event / the
              consultations/{id} doc (what the call is about). */}
          <div className="cs-ctrl" style={{ marginTop: 10 }}>
            <label className="cs-label" htmlFor="cs-notes">
              Note for the invite (optional)
            </label>
            <input
              id="cs-notes"
              className="cs-select"
              type="text"
              value={notes}
              placeholder="e.g. New home in Raipur — discovery call"
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <div className="cs-slots-label">
            Open slots — {audienceLabel(audience)} windows
            <span className="cs-slots-count">
              ({slots.length} slot{slots.length === 1 ? '' : 's'} across{' '}
              {dayGroups.length} day{dayGroups.length === 1 ? '' : 's'})
            </span>
          </div>
          <div className="cs-caption">
            Times shown in {tzLabel}. {priceLabel ? `${priceLabel} · ` : ''}
            next {horizonDays} days.
          </div>

          {availNote && (
            <div className="cs-avail-note" role="status">
              <span>{availNote}</span>
              <button
                type="button"
                className="cs-avail-retry"
                onClick={retryAvailability}
                disabled={availLoading}
              >
                {availLoading ? 'Retrying…' : 'Retry'}
              </button>
            </div>
          )}

          {availLoading && slots.length === 0 ? (
            // Still loading availability — show a loader, never the empty copy
            // (avoids a "No open slots" flash before slots resolve).
            <div className="cs-loading" role="status" aria-live="polite">
              <span className="cs-spinner" aria-hidden="true" />
              Loading available times…
            </div>
          ) : slots.length === 0 ? (
            <div className="cs-empty" role="status">
              No open slots in the next {horizonDays} days for{' '}
              {audienceLabel(audience)} windows. Try the other audience, or
              widen availability in Settings.
            </div>
          ) : (
            <div
              className="cs-slots"
              role="group"
              aria-label="Open consultation slots"
            >
              {slots.map((slot) => {
                const d = describeSlot(slot, avail.timeZone);
                const selected = selectedStart === slot.startISO;
                const intl = slot.audienceTag === 'international';
                return (
                  <button
                    type="button"
                    key={slot.startISO}
                    className="cs-slot"
                    aria-pressed={selected}
                    onClick={() => {
                      setSelectedStart(slot.startISO);
                      setResult(null);
                    }}
                  >
                    <span className="cs-slot-when">
                      <span className="cs-daychip">
                        <span className="cs-dow">{d.weekday}</span>
                        <span className="cs-dnum">{d.dayNum}</span>
                      </span>
                      <span className="cs-time">
                        <span className="cs-big">{d.time}</span>
                        <span className="cs-sm">
                          {d.monthShort} · {durationMin} min with {hostName}
                        </span>
                      </span>
                      <span
                        className={`cs-aud-chip ${intl ? 'cs-intl' : 'cs-in'}`}
                      >
                        <span aria-hidden="true">
                          {AUDIENCE_FLAG[
                            slot.audienceTag === 'international'
                              ? 'international'
                              : 'indian'
                          ]}
                        </span>{' '}
                        {audienceLabel(slot.audienceTag)}
                      </span>
                    </span>
                    <span className="cs-pick">
                      <IcCheck />
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {result && (
            <div
              className={`cs-status ${
                result.status === 'booked'
                  ? 'cs-status-ok'
                  : result.status === 'not_connected'
                    ? 'cs-status-pending'
                    : 'cs-status-error'
              }`}
              role="status"
            >
              {result.message}
              {result.status === 'booked' && result.meetLink && (
                <>
                  {' '}
                  <a
                    href={result.meetLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontWeight: 700 }}
                  >
                    Open the Meet link ↗
                  </a>
                </>
              )}
            </div>
          )}

          <div className="cs-foot">
            <div className="cs-helper">
              <b>Books for the client</b> · creates the calendar event + Meet
              link · stamps the record · posts to the thread — all in one tap.
            </div>
            <button
              type="button"
              className="cs-book-btn"
              disabled={!selectedStart || phase === 'booking'}
              onClick={onBook}
            >
              {phase === 'booking' ? 'Booking…' : 'Book'}
              <IcArrow />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

export default BookConsultPanel;
